import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Play, Pause, Upload, Volume2, Check } from 'lucide-react';
import { getBackendUrl } from '../../../utils/api';

const API = `${getBackendUrl()}/api`;
const BUILTIN_FALLBACK = '/sounds/notification.mp3';

export default function NotificationSoundSettings() {
  const [defaultSounds, setDefaultSounds] = useState([]);
  const [selection, setSelection] = useState({
    type: 'default',
    default_id: null,
    url: null,
    volume: 0.7,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const [message, setMessage] = useState(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [soundsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/notification-sounds`),
        axios.get(`${API}/restaurant/settings`),
      ]);
      setDefaultSounds(soundsRes.data || []);
      const ns = settingsRes.data?.notification_sound;
      if (ns) {
        setSelection({
          type: ns.type || 'default',
          default_id: ns.default_id || null,
          url: ns.url || null,
          volume: typeof ns.volume === 'number' ? ns.volume : 0.7,
        });
      }
    } catch (err) {
      console.error('Erro ao carregar sons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
  };

  const play = (url, key) => {
    try {
      if (playingKey === key && audioRef.current) {
        stopAudio();
        return;
      }
      stopAudio();
      const src = url || BUILTIN_FALLBACK;
      audioRef.current = new Audio(src);
      audioRef.current.volume = selection.volume;
      audioRef.current.onended = () => setPlayingKey(null);
      audioRef.current.play().catch(() => setPlayingKey(null));
      setPlayingKey(key);
    } catch (e) {
      setPlayingKey(null);
    }
  };

  const selectDefault = (sound) => {
    setSelection({ ...selection, type: 'default', default_id: sound.id, url: sound.url });
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Áudio muito grande. Máximo 2MB.' });
      return;
    }
    try {
      setUploading(true);
      setMessage(null);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/restaurant/notification-sound/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelection({ ...selection, type: 'custom', default_id: null, url: res.data.url });
      setMessage({ type: 'success', text: 'Som próprio enviado! Não esqueça de salvar.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erro ao enviar áudio' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await axios.put(`${API}/restaurant/settings`, { notification_sound: selection });
      setMessage({ type: 'success', text: 'Som de notificação salvo com sucesso!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const isCustomSelected = selection.type === 'custom' && !!selection.url;

  return (
    <div className="glass-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
          <Bell className="text-white" size={22} />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-white">Som de Novo Pedido</h2>
          <p className="text-sm text-gray-400">Escolha o som tocado quando um novo pedido chega</p>
        </div>
      </div>

      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-xl text-sm font-medium ${
            message.type === 'success'
              ? 'bg-green-500/15 text-green-300 border border-green-500/30'
              : 'bg-red-500/15 text-red-300 border border-red-500/30'
          }`}
          data-testid="notif-sound-message"
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 py-10 text-center">Carregando...</div>
      ) : (
        <>
          {/* Volume */}
          <div className="mb-6">
            <label className="flex items-center gap-2 text-base font-medium text-gray-300 mb-3">
              <Volume2 size={18} /> Volume ({Math.round(selection.volume * 100)}%)
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={selection.volume}
              onChange={(e) => setSelection({ ...selection, volume: parseFloat(e.target.value) })}
              className="w-full accent-amber-500"
              data-testid="volume-slider"
            />
          </div>

          {/* Default sounds */}
          <div className="mb-6">
            <p className="text-base font-medium text-gray-300 mb-3">Sons padrão</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {defaultSounds.length === 0 && (
                <p className="text-gray-500 text-sm">Nenhum som padrão disponível no momento.</p>
              )}
              {defaultSounds.map((sound) => {
                const selected = selection.type === 'default' && selection.default_id === sound.id;
                return (
                  <div
                    key={sound.id}
                    onClick={() => selectDefault(sound)}
                    className={`cursor-pointer rounded-xl border p-3 flex items-center justify-between gap-2 transition ${
                      selected
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                    data-testid={`default-sound-${sound.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); play(sound.url, sound.id); }}
                        className="w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center text-white"
                        title="Ouvir"
                      >
                        {playingKey === sound.id ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <span className="text-white text-sm truncate">{sound.name}</span>
                    </div>
                    {selected && <Check size={18} className="text-amber-400 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Custom sound */}
          <div className="mb-6">
            <p className="text-base font-medium text-gray-300 mb-3">Seu próprio som</p>
            <div
              className={`rounded-xl border p-4 ${
                isCustomSelected ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 bg-white/5'
              }`}
            >
              <p className="text-sm text-gray-400 mb-3">
                Envie um áudio da sua galeria ou grave um novo (no celular, o botão abaixo permite gravar). Máx. 2MB.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleUpload}
                  className="hidden"
                  id="custom-sound-input"
                  data-testid="custom-sound-input"
                />
                <label
                  htmlFor="custom-sound-input"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium cursor-pointer transition"
                >
                  <Upload size={18} /> {uploading ? 'Enviando...' : 'Enviar áudio'}
                </label>
                {isCustomSelected && (
                  <>
                    <button
                      type="button"
                      onClick={() => play(selection.url, 'custom')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-medium"
                    >
                      {playingKey === 'custom' ? <Pause size={16} /> : <Play size={16} />} Ouvir
                    </button>
                    <span className="inline-flex items-center gap-1 text-amber-400 text-sm font-medium">
                      <Check size={16} /> Som próprio selecionado
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => play(selection.url, 'test')}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition"
              data-testid="test-sound-btn"
            >
              {playingKey === 'test' ? <Pause size={18} /> : <Play size={18} />} Testar som atual
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold shadow-lg hover:opacity-90 transition disabled:opacity-50"
              data-testid="save-notif-sound-btn"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
